import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ContentDetailCard, InPageHeader,
  InfoBar,
  InstructionsPopup,
  Loader,
  NoContentCard,
  SecondaryButton,
  getLocalStorage,
  getMediaBasePath,
  getResourceCategory,
  practicalProjectToProjectFile,
  pxToRem, pxTovW, resourceTypeName,
  useCommonServiceClientContext
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { Resource_ResourceEnum } from '@protos/content_management/content.db_pb';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { useAppDispatch } from '../../../reduxStore/reduxHooks';
import Heading from './Heading';
import { interactionEvent } from '../../Auth/auth.events';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import {
  AssessmentContent,
  ProjectResourceInfo,
} from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  GetProjectMarksData,
  ProjectGroupMarkSummary,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { SessionModeEnum } from '@protos/learning_management/lms.db_pb';
import { onResourceClick } from '../../../utils/resource';
import { View_Project } from '../../../routeHandling/RoutesNomenclature';
import { GroupDetails } from '../components/GroupDetails';
import { ProjectEvidenceDialog } from '../components/ProjectEvidenceDialog';
import { ProjectGroupMarksDialog } from '../components/ProjectGroupMarksDialog';
import ProjectIndividualMarksDialog from '../components/ProjectIndividualMarksDialog';
import { ProjectMarksRemarksModal } from '../components/ProjectMarksRemarksModal';
import { styles } from './styles';

type PendingMarksSubmission =
  | { type: 'group'; obtainedMarks: number }
  | {
    type: 'individual';
    marks: { studentId: string; obtainedMarks: number }[];
  };

export default function ViewProjectDetails() {
  const [contentLoading, setContentLoading] = useState<
    'loading' | 'completed' | 'error' | 'invalid session'
  >('loading');
  const [projectDetails, setProjectDetails] = useState<AssessmentContent>();
  const [projectMarks, setProjectMarks] = useState<GetProjectMarksData>();
  const [selectedGroup, setSelectedGroup] = useState<ProjectGroupMarkSummary>();
  const [groupMarksOpen, setGroupMarksOpen] = useState(false);
  const [individualMarksOpen, setIndividualMarksOpen] = useState(false);
  const [marksSubmitting, setMarksSubmitting] = useState(false);
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [evidenceGroup, setEvidenceGroup] = useState<ProjectGroupMarkSummary>();
  const [learningOutcomesOpen, setLearningOutcomesOpen] = useState(false);
  const [pendingMarksSubmission, setPendingMarksSubmission] =
    useState<PendingMarksSubmission>();
  const { isOffline } = useDownloadContext();
  const dispatch = useAppDispatch();
  const {
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { assessment_id } = useParams();
  const teacher_id = getLocalStorage('userId');
  const navigate = useNavigate();

  const openResource = (
    resource: ProjectResourceInfo,
    /** The group whose submitted work to open the resource on, when reviewing one. */
    group?: ProjectGroupMarkSummary
  ) => {
    //~ Keyed by resource, not ordered - a project with several practicals
    //~ carries one response each and [0] is not necessarily this one.
    const savedProject = group?.practicalResponses.find(
      (response) => response.resourceId === resource.resourceId
    )?.project;
    //~ The resource page exits view mode with a history `navigate(-1)`. Re-assert
    //~ this project screen as the entry directly beneath the resource so that
    //~ back-step always returns here, regardless of how the user got to this
    //~ screen (Home project list, Project Dashboard, deep link, ...).
    if (assessment_id) {
      navigate(`${View_Project}/${assessment_id}`);
    }
    onResourceClick(
      navigate,
      {
        resourceId: resource.resourceId,
        lessonId: resource.lessonId,
        subjectId: projectDetails?.assessment?.subjectId,
        chapterId: projectDetails?.assessment?.chapterId,
        assessmentId: assessment_id ? Number(assessment_id) : undefined,
        practicalReview: group && {
          project: savedProject
            ? practicalProjectToProjectFile(savedProject)
            : undefined,
        },
      },
      SessionModeEnum.SESSION_MODE_PROJECT,
      'view'
    );
  };


  const viewGroupProject = (group: ProjectGroupMarkSummary) => {
    const practicalResource = projectResources.find(
      (resource) =>
        resource.resourceType === Resource_ResourceEnum.RESOURCE_TYPE_PRACTICAL
    );
    if (!practicalResource) {
      dispatch(
        setToastInfo({
          label: 'This project has no practical work to review',
          variant: 'info',
          open: true,
        })
      );
      return;
    }
    openResource(practicalResource, group);
  };

  const fetchProjectDetails = async () => {
    if (!teacher_id || !assessment_id) {
      setContentLoading('invalid session');
      return;
    }
    try {
      setContentLoading('loading');
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            assessmentId: BigInt(assessment_id),
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
          }
        );
      if (!response?.data) {
        throw new Error(response?.message || 'Project details not found');
      }
      setProjectDetails(response.data);
      setContentLoading('completed');
    } catch (error) {
      console.log(error);
      dispatch(
        setToastInfo({
          label: 'Error fetching project details',
          variant: 'error',
          open: true,
        })
      );
      setContentLoading('error');
    }
  };

  /** Grading screen data: every group, its members and their marks. */
  const fetchProjectMarks = async () => {
    if (!assessment_id) {
      return;
    }
    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getProjectMarks(
          {
            assessmentId: BigInt(assessment_id),
          }
        );
      setProjectMarks(response?.data);
    } catch (error) {
      console.log(error);
      setProjectMarks(undefined);
    }
  };

  useEffect(() => {
    fetchProjectDetails();
    fetchProjectMarks();
  }, [assessment_id]);

  const closeMarksDialogs = () => {
    setGroupMarksOpen(false);
    setIndividualMarksOpen(false);
    setSelectedGroup(undefined);
  };

  const closeRemarksModal = () => {
    setRemarksModalOpen(false);
    setPendingMarksSubmission(undefined);
  };

  /**
   * Both write endpoints answer with the state of every group they touched,
   * so the screen refreshes from the response instead of re-reading the list.
   */
  const applyUpdatedGroups = (updatedGroups: ProjectGroupMarkSummary[]) => {
    if (!updatedGroups.length) {
      fetchProjectMarks();
      return;
    }
    setProjectMarks((previous) => {
      if (!previous) {
        return previous;
      }
      const next = previous.clone();
      next.groups = previous.groups.map(
        (group) =>
          updatedGroups.find(
            (updated) => updated.studentGroupId === group.studentGroupId
          ) ?? group
      );
      return next;
    });
  };

  /** Surfaces the server's own message; connect puts it on error.message. */
  const showMarksError = (error: unknown) => {
    console.log(error);
    dispatch(
      setToastInfo({
        label:
          error instanceof Error && error.message
            ? error.message
            : 'Error submitting marks',
        variant: 'error',
        open: true,
      })
    );
  };

  const onMarksSubmitted = (updatedGroups: ProjectGroupMarkSummary[]) => {
    closeMarksDialogs();
    dispatch(
      setToastInfo({
        label: 'Marks submitted successfully',
        variant: 'success',
        open: true,
      })
    );
    applyUpdatedGroups(updatedGroups);
  };

  /** One mark written to every active member of the group. */
  const submitGroupMarks = async (obtainedMarks: number, remarks?: string) => {
    if (!selectedGroup || !assessment_id) {
      return;
    }
    try {
      setMarksSubmitting(true);
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.updateProjectGroupMarks(
          {
            assessmentId: BigInt(assessment_id),
            studentGroupId: selectedGroup.studentGroupId,
            obtainedMarks: obtainedMarks,
            remarks: remarks ? [remarks] : [],
          }
        );
      onMarksSubmitted(response?.data?.groups ?? []);
    } catch (error) {
      showMarksError(error);
    } finally {
      setMarksSubmitting(false);
    }
  };

  /** Per student marks; the server recalculates the group average from them. */
  const submitIndividualMarks = async (
    marks: { studentId: string; obtainedMarks: number }[],
    remarks?: string
  ) => {
    if (!selectedGroup || !assessment_id || !marks.length) {
      return;
    }
    try {
      setMarksSubmitting(true);
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.updateProjectStudentMarks(
          {
            assessmentId: BigInt(assessment_id),
            studentGroupId: selectedGroup.studentGroupId,
            studentMarks: marks.map((mark) => ({
              studentId: BigInt(mark.studentId),
              obtainedMarks: mark.obtainedMarks,
              remarks: remarks ? [remarks] : [],
            })),
          }
        );
      onMarksSubmitted(response?.data?.groups ?? []);
    } catch (error) {
      showMarksError(error);
    } finally {
      setMarksSubmitting(false);
    }
  };

  /** True once any active member of the group already has a stored mark. */
  const isGroupAlreadyMarked = (group?: ProjectGroupMarkSummary) =>
    (group?.gradedStudentCount ?? 0) > 0;

  /** True if any of the students being submitted already has a stored mark. */
  const isIndividualAlreadyMarked = (
    group: ProjectGroupMarkSummary | undefined,
    marks: { studentId: string; obtainedMarks: number }[]
  ) =>
    marks.some((mark) =>
      group?.studentMarks.find(
        (student) => student.studentId.toString() === mark.studentId
      )?.isGraded
    );

  /** Re-grading a group requires a remark explaining the change; first-time entry does not. */
  const handleGroupMarksProceed = (obtainedMarks: number) => {
    if (isGroupAlreadyMarked(selectedGroup)) {
      setPendingMarksSubmission({ type: 'group', obtainedMarks });
      setGroupMarksOpen(false);
      setRemarksModalOpen(true);
      return;
    }
    submitGroupMarks(obtainedMarks);
  };

  /** Re-grading any already-marked student requires a remark explaining the change. */
  const handleIndividualMarksProceed = (
    marks: { studentId: string; obtainedMarks: number }[]
  ) => {
    if (isIndividualAlreadyMarked(selectedGroup, marks)) {
      setPendingMarksSubmission({ type: 'individual', marks });
      setIndividualMarksOpen(false);
      setRemarksModalOpen(true);
      return;
    }
    submitIndividualMarks(marks);
  };

  const handleRemarksSubmit = (remarks: string) => {
    if (!pendingMarksSubmission) {
      return;
    }
    if (pendingMarksSubmission.type === 'group') {
      submitGroupMarks(pendingMarksSubmission.obtainedMarks, remarks);
    } else {
      submitIndividualMarks(pendingMarksSubmission.marks, remarks);
    }
    closeRemarksModal();
  };

  const projectResources = projectDetails?.projectResources ?? [];
  //~ The union of the project's resources' outcomes, in resource order.
  const learningOutcomes = projectDetails?.learningOutcomes ?? [];
  const totalResourceCount = projectDetails?.projectResources?.length ?? 0;
  const estimatedTimeInMin =
    projectDetails?.assessment?.estimatedTimeInMin ?? 0;
  /** class, section and subject only read as a line when all three are present. */
  const classSectionSubjectDetails = [
    [
      'Class ',
      projectDetails?.assessment?.class,
      projectDetails?.assessment?.section,
    ]
      .filter(Boolean)
      .join(''),
    projectDetails?.assessment?.subject,
  ]
    .filter(Boolean)
    .join(' | ');

  /** e.g. "6A" - grade + section, shown against each group member. */
  const gradeSection = [
    projectDetails?.assessment?.class,
    projectDetails?.assessment?.section,
  ]
    .filter(Boolean)
    .join('');

  const projectGroups = projectMarks?.groups ?? [];
  const groupMaxMarks =
    selectedGroup?.groupMaxMarks || projectMarks?.maxMarks || 0;

  const marksDialogResources = useMemo(
    () =>
      projectResources.map((resource, index) => ({
        serialNumber: `${index + 1}`,
        taskName: getResourceCategory(resource.resourceCategoryType),
        contentName: resource.title,
        duration: `${resource.estimatedTimeInMin} Min`,
      })),
    [projectResources]
  );

  /** Memoised: the dialog reseeds its inputs whenever this list changes. */
  const selectedGroupStudents = useMemo(
    () =>
      (selectedGroup?.studentMarks ?? []).map((student) => ({
        id: student.studentId.toString(),
        name: student.studentName || `Student ${student.studentId}`,
        classsection: classSectionSubjectDetails,
        totalMarks: `${groupMaxMarks}`,
        marks: student.isGraded ? `${student.obtainedMarks}` : '',
      })),
    [selectedGroup, groupMaxMarks, classSectionSubjectDetails]
  );

  /** The group's own evidence; a deleted upload is history, not evidence. */
  const groupEvidenceUploads = useMemo(
    () =>
      (evidenceGroup?.userUploads ?? []).filter((upload) => !upload.isDeleted),
    [evidenceGroup]
  );

  const openGroupMarks = (group: ProjectGroupMarkSummary) => {
    setSelectedGroup(group);
    // Max marks missing means the marks data failed to load - retry, so the
    // dialog's "open it again to retry" actually recovers.
    if (!group.groupMaxMarks && !projectMarks?.maxMarks) {
      fetchProjectMarks();
    }
    /** An ungrouped project is marked student by student, never as a team. */
    if (projectMarks?.isCollaborative) {
      setGroupMarksOpen(true);
    } else {
      setIndividualMarksOpen(true);
    }
  };

  return contentLoading === 'loading' ? (
    <Loader />
  ) : contentLoading === 'error' ? (
    <NoContentCard variant="error" icon="error" text="Error Occured" />
  ) : contentLoading === 'invalid session' ? (
    <NoContentCard variant="error" icon="error" text="Invalid session" />
  ) : (
    <Box>
      <Box sx={styles.root}>
        <InPageHeader
          title={
            <Heading
              projectDetails={projectDetails}
              classSectionSubject={classSectionSubjectDetails}
            />
          }
        />

        <Box sx={styles.mainContainer}>
          <Box sx={styles.leftPanel}>
            {learningOutcomes.length > 0 && (
              <Box
                sx={{
                  margin: {
                    xs: `${pxToRem(26)} ${pxToRem(4)} ${pxToRem(0)} ${pxToRem(
                      4
                    )}`,
                    md: 0,
                  },
                }}
              >
                <SecondaryButton
                  variant="outlined"
                  witharrow
                  fullWidth
                  onClick={async () => {
                    await interactionEvent({
                      url: 'Teacher_view_project_details',
                      context: 'Drop_down',
                      name: 'LEARNING_OUTCOME',
                      isOffline: isOffline,
                    });
                    setLearningOutcomesOpen(true);
                  }}
                >
                  <Typography
                    variant={'h5'}
                    fontWeight="bold"
                    fontFamily={'poppins'}
                  >
                    Learning Outcome
                  </Typography>
                </SecondaryButton>
              </Box>
            )}

            {projectGroups.length > 0 && (
              <GroupDetails
                groups={projectGroups}
                maxMarks={projectMarks?.maxMarks}
                heading={
                  projectMarks?.isCollaborative ? undefined : 'Student Marks'
                }
                gradeSection={gradeSection}
                onGiveMarks={openGroupMarks}
                onViewProject={viewGroupProject}
                onViewEvidence={setEvidenceGroup}
              />
            )}

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <InfoBar
                contentList={[
                  {
                    iconName: 'questions',
                    heading: totalResourceCount,
                    subHeading: 'Resources',
                  },
                  {
                    iconName: 'clock',
                    heading: estimatedTimeInMin,
                    subHeading: 'Minutes',
                  },
                  {
                    iconName: 'marks',
                    heading: projectDetails?.assessment?.maxMarks ?? 0,
                    subHeading: 'Max Marks',
                  },
                ]}
              />
            </Box>
          </Box>

          <Box sx={styles.rightPanel}>
            <Box sx={styles.rightPanelHeading}>
              <Typography variant="h2" sx={{
                fontSize: { xs: pxToRem(18), md: pxToRem(21), lg: pxTovW(24) },
                fontWeight: 400
              }}>Resources</Typography>
            </Box>

            {projectResources.length > 0 ? (
              <Box sx={styles.cardsContainer}>
                {projectResources.map((resource) => (
                  <ContentDetailCard
                    key={`${resource.lessonId}-${resource.resourceId}`}
                    variant="large"
                    tagName={getResourceCategory(resource.resourceCategoryType)}
                    image={getMediaBasePath(
                      resource.posterImageUrl,
                      'processedMediaBucket'
                    )}
                    heading={resource.title}
                    iconDetails={iconDetails(resource)}
                    onClick={() => openResource(resource)}
                    rootStyle={{
                      width: '100%',
                      backgroundColor: 'common.white',
                      cursor: 'pointer',
                    }}
                    showCompletedRibbon={false}
                    showVideoIcon={
                      resource.resourceType ===
                      Resource_ResourceEnum.RESOURCE_TYPE_VIDEO
                    }
                  />
                ))}
              </Box>
            ) : (
              <NoContentCard
                variant="info"
                icon="laboratory"
                text="No Content Available"
              />
            )}
          </Box>
        </Box>
      </Box>

      <ProjectGroupMarksDialog
        popupOpen={groupMarksOpen}
        groupName={selectedGroup?.groupName}
        memberCount={selectedGroup?.activeStudentCount}
        maxMarks={groupMaxMarks}
        resources={marksDialogResources}
        individualClick={() => {
          setGroupMarksOpen(false);
          setIndividualMarksOpen(true);
        }}
        popupCloseHandler={closeMarksDialogs}
        proceedButtonData={{
          onClick: handleGroupMarksProceed,
          disabled: marksSubmitting,
        }}
      />

      <ProjectIndividualMarksDialog
        popupOpen={individualMarksOpen}
        groupName={selectedGroup?.groupName}
        studentList={selectedGroupStudents}
        popupCloseHandler={closeMarksDialogs}
        proceedButtonData={{
          onClick: handleIndividualMarksProceed,
          disabled: marksSubmitting,
        }}
      />

      <ProjectMarksRemarksModal
        popupOpen={remarksModalOpen}
        popupCloseHandler={closeRemarksModal}
        submitting={marksSubmitting}
        onSubmit={handleRemarksSubmit}
      />

      <ProjectEvidenceDialog
        open={Boolean(evidenceGroup)}
        groupName={evidenceGroup?.groupName}
        uploads={groupEvidenceUploads}
        handleClose={() => setEvidenceGroup(undefined)}
      />

      <InstructionsPopup
        popupText={learningOutcomes}
        open={learningOutcomesOpen}
        handleClose={() => setLearningOutcomesOpen(false)}
      />
    </Box>
  );
}

const iconDetails = (resource: ProjectResourceInfo) => {
  const retValue = [
    {
      iconName: 'clock',
      text: `${resource.estimatedTimeInMin} Min`,
    },
    {
      iconName:
        resourceTypeName(resource.resourceType).name === 'Practical'
          ? 'Project_green_icon'
          : resourceTypeName(resource.resourceType).icon,
      text: resourceTypeName(resource.resourceType).name,
    },
  ];

  return retValue;
};
